<template>
  <section>
    <BaseDialog
      v-if="simpleCreation"
      :width="'375px'"
      :show-text="false"
      :text-align="'left'"
      :footer-align="'flex-end'"
      :title="$t('adminTitles.EnterCarrierName')"
      :confirm-button-text="$t('adminTitles.continue')"
      :cancel-button-text="$t('buttons.cancel')"
      :confirm-loading="btnLoading"
      @close="$emit('close')"
      @confirm="submitSimpleForm"
    >
      <el-form :model="simpleForm" :rules="simpleFormRules" ref="form" label-position="top"
               class="mb_32">
        <el-form-item class="dialog-modal__input" :label="$t('adminTitles.CarrierName')"
                      prop="name">
          <el-input :placeholder="$t('adminTitles.CarrierName')" v-model="simpleForm.name"/>
        </el-form-item>
      </el-form>
    </BaseDialog>
    <BaseSideModal v-if="!simpleCreation" :width="'1000px'" @close="closeDialogOpen = true">
      <template #title>
        <span>{{
            editableCompanyId ? $t('insuranceCompanies.EditInsuranceCompany') : $t('insuranceCompanies.AddNewInsuranceCompany')
          }}</span>
      </template>
      <div class="company-modal" v-if="!modalLoading">
        <div class="company-modal__tabs">
          <BaseTabs>
            <BaseTabsTab :active="activeTab === 'main'" @click="changeTab('main')">
              {{ $t('insuranceCompanies.MainInformation') }}
            </BaseTabsTab>
            <BaseTabsTab v-if="showPortalAccounts" :active="activeTab === 'portalAccounts'"
                         @click="changeTab('portalAccounts')">
              {{ $t('carriers.import.tab.PortalAccounts') }}
            </BaseTabsTab>
            <BaseTabsTab v-if="!isSalesHub" :active="activeTab === 'addingInsured'"
                         @click="changeTab('addingInsured')">
              {{ $t('insuranceCompanies.FieldSettingsForAddingInsureds') }}
            </BaseTabsTab>
            <BaseTabsTab v-if="!isSalesHub" :active="activeTab === 'detachingInsured'"
                         @click="changeTab('detachingInsured')">
              {{ $t('insuranceCompanies.FieldSettingsForDetachingInsureds') }}
            </BaseTabsTab>
          </BaseTabs>
        </div>

        <div v-if="activeTab === 'addingInsured' && !isSalesHub">
          <p class="company-modal__title">
            {{ $t('insuranceCompanies.SelectFieldsWhich') }}
          </p>
          <p class="company-modal__description">
            {{ $t('insuranceCompanies.HereYouCanSpecify') }}
          </p>
        </div>

        <div v-if="activeTab === 'detachingInsured' && !isSalesHub">
          <p class="company-modal__title">
            {{ $t('insuranceCompanies.SelectDeactivateFieldsTitle') }}
          </p>
          <p class="company-modal__description">
            {{ $t('insuranceCompanies.SelectDeactivateFieldsDescription') }}
          </p>
        </div>

        <div :class="{'company-modal__tab_hidden' : activeTab !== 'main'}">
          <MainInformationForm
            ref="companyForm"
            :editable-company="editableCompany"
            @onSubmit="submitForm"
          />
        </div>

        <PortalAccountsTab
          v-if="activeTab === 'portalAccounts' && showPortalAccounts"
          :insurance-company-id="editableCompanyId"
          :carrier-name="(editableCompany && editableCompany.name) || ''"
          :carrier-code="(editableCompany && editableCompany.carrierCode) || null"
          :initial-account-id="initialAccountId"
        />

        <FieldSettings
          v-if="activeTab === 'addingInsured' && !isSalesHub"
          :key="1"
          :scans-fields="addingFields.scansFields"
          :required-fields="addingFields.requiredFields"
          :additional-fields="addingFields.optionalFields"
          :selected-fields.sync="selectedAddingFields"
          :selected-scans.sync="selectedAddingScans"
          :selected-required-fields.sync="selectedAddingRequiredFields"
        />

        <FieldSettings
          v-if="activeTab === 'detachingInsured' && !isSalesHub"
          :key="2"
          :scans-fields="detachingFields.scansFields"
          :required-fields="detachingFields.requiredFields"
          :additional-fields="detachingFields.optionalFields"
          :selected-fields.sync="selectedDetachingFields"
          :selected-scans.sync="selectedDetachingScans"
          :selected-required-fields.sync="selectedDetachingRequiredFields"
        />
      </div>

      <template #footer>
        <BaseButton type="outline" @click="$emit('close')">
          {{ $t('adminTitles.cancel') }}
        </BaseButton>
        <BaseButton :loading="btnLoading" @click="submitForm">
          {{
            editableCompanyId ? $t('employees.SaveChanges') : $t('insuranceCompanies.AddCompany')
          }}
        </BaseButton>
      </template>
    </BaseSideModal>

    <BaseDialog
      v-if="closeDialogOpen"
      :width="'411px'"
      :text-align="'left'"
      :footer-align="'flex-end'"
      :title="$t('insuranceCompanies.CancelInsuranceCompanyEditingTitle')"
      :text="$t('insuranceCompanies.CancelInsuranceCompanyEditingText')"
      :confirm-button-text="$t('adminTitles.continue')"
      :cancel-button-text="$t('adminTitles.cancel')"
      @close="closeDialogOpen = false"
      @confirm="$emit('close')"
    />
  </section>
</template>

<script>
import {mapGetters} from 'vuex';
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseTabs from '@/views/RenovaDashboard/components/ui-kit/BaseTabs';
import BaseTabsTab from '@/views/RenovaDashboard/components/ui-kit/BaseTabsTab';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';
import {
  AddInsuredFields,
  DeactivateInsuredFields
} from '@/views/RenovaDashboard/SettingsParts/parts/InsuranceCompanies/components/constants';
import {GET_ACTIVE_INSURANCE_COMPANIES, GET_DICTIONARY} from '@/store/actions/dictionary';
import MainInformationForm from './MainInformationForm';
import FieldSettings from './FieldSettings';
import PortalAccountsTab from './carrier-import/PortalAccountsTab';

export default {
  name: 'NewInsuranceCompanyModal',
  components: {
    BaseSideModal,
    BaseTabs,
    BaseTabsTab,
    BaseButton,
    BaseDialog,
    MainInformationForm,
    FieldSettings,
    PortalAccountsTab,
  },
  props: {
    editableCompanyId: String,
    simpleCreation: {
      type: Boolean,
      default: false,
    },
    initialName: {
      type: String,
      default: '',
    },
    /** Open the modal straight on this tab — the Carriers table's Connect button. */
    initialTab: {
      type: String,
      default: 'main',
    },
    /** With initialTab 'portalAccounts', open this account's own window too. */
    initialAccountId: {
      type: String,
      default: null,
    },
  },
  emits: ['close', 'onCompanyCreate'],
  computed: {
    ...mapGetters(['providerType', 'scraperEnabled']),
    isSalesHub() {
      return this.providerType === 'sales_hub';
    },
    /**
     * Only for a carrier that already exists: a portal login needs a carrier to
     * belong to, and there is none until the first save.
     */
    showPortalAccounts() {
      return Boolean(this.editableCompanyId) && this.scraperEnabled && !this.isSalesHub;
    },
  },
  data() {
    return {
      activeTab: this.initialTab || 'main',
      btnLoading: false,
      modalLoading: false,
      closeDialogOpen: false,
      selectedAddingFields: [],
      selectedAddingScans: [],
      selectedAddingRequiredFields: [],
      selectedDetachingFields: [],
      selectedDetachingScans: [],
      selectedDetachingRequiredFields: [],
      addingFields: AddInsuredFields,
      detachingFields: DeactivateInsuredFields,
      editableCompany: null,
      serverAddInsuredFields: [],
      serverDetachInsuredFields: [],
      simpleForm: {
        name: '',
      },
      simpleFormRules: {
        name: [
          {
            required: true,
            message: this.$t('adminTitles.FieldIsRequired'),
          },
        ],
      },
    };
  },
  created() {
    if (this.simpleCreation && this.initialName) {
      this.simpleForm.name = this.initialName;
    }
    if (this.editableCompanyId) {
      this.modalLoading = true;
      this.$store.dispatch('getInsuranceCompany', this.editableCompanyId)
        .then((res) => {
          this.editableCompany = res;
          this.serverAddInsuredFields = res.attributes.addInsured.map((field) => field.name);
          this.serverDetachInsuredFields = res.attributes.detachInsured.map((field) => field.name);
          this.selectedAddingRequiredFields = res.attributes.addInsured
            .filter((field) => !this.addingFields.requiredFields.includes(field.name) && field.isRequired)
            .map((field) => field.name);
          this.selectedDetachingRequiredFields = res.attributes.detachInsured
            .filter((field) => !this.detachingFields.requiredFields.includes(field.name) && field.isRequired)
            .map((field) => field.name);
          this.processFields(res.attributes.addInsured, res.attributes.detachInsured);
          this.modalLoading = false;
        });
    }
  },
  methods: {
    changeTab(tab) {
      // If user is on Sales Hub and tries to access hidden tabs, keep them on main tab
      if (this.isSalesHub && (tab === 'addingInsured' || tab === 'detachingInsured')) {
        return;
      }
      if (tab === 'portalAccounts' && !this.showPortalAccounts) {
        return;
      }
      this.activeTab = tab;
    },
    processFields(addInsuredFields, detachInsuredFields) {
      this.selectedAddingFields = addInsuredFields
        .filter((field) => field.type !== 'FILE')
        .map((field) => field.name);

      this.selectedAddingScans = addInsuredFields
        .filter((field) => field.type === 'FILE')
        .map((field) => field.name);

      this.selectedDetachingFields = detachInsuredFields
        .filter((field) => field.type !== 'FILE')
        .map((field) => field.name);

      this.selectedDetachingScans = detachInsuredFields
        .filter((field) => field.type === 'FILE')
        .map((field) => field.name);
    },
    getRemovedFields(serverFields, selectedFields, selectedScans) {
      const currentSelected = [...selectedFields, ...selectedScans];
      return serverFields.filter((field) => !currentSelected.includes(field));
    },
    async submitForm() {
      this.$refs.companyForm.$refs.form.validate((valid) => {
        if (!valid) {
          this.activeTab = 'main';
        } else {
          this.btnLoading = true;

          const addInsuredAttributeDelete = this.getRemovedFields(
            this.serverAddInsuredFields,
            this.selectedAddingFields,
            this.selectedAddingScans
          );
          const detachInsuredAttributeDelete = this.getRemovedFields(
            this.serverDetachInsuredFields,
            this.selectedDetachingFields,
            this.selectedDetachingScans
          );
          const addInsuredAttributeSave = [...this.selectedAddingFields, ...this.selectedAddingScans].map((field) => ({
            name: field,
            isRequired: this.addingFields.requiredFields.includes(field) || this.selectedAddingRequiredFields.includes(field),
          }));

          const detachInsuredAttributeSave = [...this.selectedDetachingFields, ...this.selectedDetachingScans].map((field) => ({
            name: field,
            isRequired: this.detachingFields.requiredFields.includes(field) || this.selectedDetachingRequiredFields.includes(field),
          }));

          this.$store.dispatch('saveInsuranceCompany', {
            insuranceCompanyId: this.editableCompanyId,
            ...this.$refs.companyForm.form,
            attributes: {
              addInsuredAttributeSave,
              detachInsuredAttributeSave,
              addInsuredAttributeDelete,
              detachInsuredAttributeDelete,
            },
          })
            .then(async (res) => {
              await this.$store.dispatch(GET_ACTIVE_INSURANCE_COMPANIES);
              this.$emit('onCompanyCreate', res);
              this.$emit('close');
            });
        }
      });
    },
    async submitSimpleForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.btnLoading = true;
          this.$store.dispatch('saveInsuranceCompany', {
            ...this.simpleForm,
          })
            .then(async (res) => {
              await this.$store.dispatch(GET_ACTIVE_INSURANCE_COMPANIES);
              this.$emit('onCompanyCreate', res);
              this.$emit('close');
            });
        }
      });
    },
  },
};
</script>

<style lang="scss">
.company-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// Four tabs, every one of them visible. The modal is 1000px so the English strip
// (~900px with its padding) sits on one row; Spanish and Portuguese run longer, so
// the strip wraps onto a second row rather than clip -- BaseTabs is a bare flex row
// with no wrap and BaseTabsTab is white-space: nowrap, and the earlier answer, a
// strip that scrolled with its scrollbar hidden, left the last tab simply gone.
// Fixed here rather than in BaseTabs, which every other screen shares.
.company-modal__tabs {
  display: flex;
  margin-bottom: 8px;

  ::v-deep .base-tabs {
    float: none;
    flex-wrap: wrap;
    max-width: 100%;
  }
}

.company-modal__tab_hidden {
  display: none;
}

.company-modal__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
  color: var(--solid-black-purple);
}

.company-modal__description {
  color: var(--solid-black-purple);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  margin-top: 8px;
}
</style>
